import { useCallback, useMemo } from "react";
import {
  useSearchParams,
  useNavigate,
  createSearchParams,
} from "react-router-dom";

import { Row } from "@covertree/ui/table/types";

import { useAuthContext } from "@covertree/auth";
import CognitoUserGroup from "@enums/CognitoUserGroup";
import { getAddressLine1WithoutUnit } from "@utils";

import { InsuranceAdoptionStatus, Resident } from "@graphql/api";
import { BASE_RESIDENTS_PATH, RESIDENTS_ROUTES } from "@routing/Residents";

import { mapResidentInsuranceAdoptionStatus } from "../utils";

export const useMappedSearchResults = (residents: Resident[]) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { hasGroup } = useAuthContext();

  const openResidentDetails = useCallback(
    (id: string) => {
      navigate({
        pathname: `/${BASE_RESIDENTS_PATH}/${RESIDENTS_ROUTES.RESIDENT}/${encodeURIComponent(id)}`,
        search: createSearchParams(searchParams).toString(),
      });
    },
    [searchParams, setSearchParams]
  );

  const openReviewDetails = useCallback(
    (id: string) => {
      navigate({
        pathname: `/${BASE_RESIDENTS_PATH}/${RESIDENTS_ROUTES.REVIEW}/${encodeURIComponent(id)}`,
        search: createSearchParams(searchParams).toString(),
      });
    },
    [searchParams, setSearchParams]
  );

  const canSeeReviewDetails = useMemo(
    () =>
      hasGroup([
        CognitoUserGroup.CoverTreeExecutive,
        CognitoUserGroup.CoverTreeEmployee,
        CognitoUserGroup.CommunityPortfolioExecutive,
        CognitoUserGroup.CommunityManager,
      ]),
    []
  );

  return useMemo(() => {
    if (!residents) {
      return;
    }

    return residents.map<Row>(({ siteAddress, ...resident }) => ({
      id: resident.id,
      data: [
        resident.residentId,
        `${resident.firstName} ${resident.lastName}`,
        `${[
          getAddressLine1WithoutUnit(siteAddress.addressLine1),
          siteAddress.city,
          siteAddress.state,
          siteAddress.zipCode,
        ].join(", ")}`,
        siteAddress.lotUnit,
        mapResidentInsuranceAdoptionStatus(resident.insuranceAdoption.status),
      ],
      onClickRow: () => openResidentDetails(resident.id),
      actionMenuItems: [
        {
          label: "View Resident Details",
          onClick: () => openResidentDetails(resident.id),
        },
        ...(resident.insuranceAdoption.status ===
          InsuranceAdoptionStatus.UnderReview && canSeeReviewDetails
          ? [
              {
                label: "View Review Details",
                onClick: () => openReviewDetails(resident.id),
              },
            ]
          : []),
      ],
    }));
  }, [residents]);
};
